import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { Button, Text } from '@belong/ui';
import { HomeAccessInstructionsModal } from 'accounts/components/home-access-instructions-modal/home-access-instructions-modal';
import { DELIVERY_METHODS } from 'accounts/constants/home-keys';
import { HOME_KEYS_STRINGS } from 'accounts/constants/strings/home-keys.strings';
import { useHomeAccessInstructions } from 'accounts/hooks/home-access-instructions';
import { useHomeownerInspections } from 'accounts/hooks/homeowners/use-homeowner-inspections';
import type { UnitSummaryModel } from 'api/models';
import clsx from 'clsx';

type Props = {
  unit: UnitSummaryModel;
  setPropertySectionTitleVisibilityByProperty: Dispatch<
    SetStateAction<
      Record<
        string,
        Partial<
          Record<
            | 'upcoming-appointments'
            | 'home-access-instructions'
            | 'home-improvements-tracking',
            boolean
          >
        >
      >
    >
  >;
};

export function HomeAccessInstructionsSection({
  unit,
  setPropertySectionTitleVisibilityByProperty,
}: Props) {
  const [
    isHomeAccessInstructionsModalOpen,
    setIsHomeAccessInstructionsModalOpen,
  ] = useState(false);
  const { isLoadingInspections, inspections } = useHomeownerInspections();
  // `basicInfo` and its `unitId` are both optional in the generated model, but
  // this section only renders for a unit out of the account's property list,
  // where both are populated — every read below already assumed so.
  const unitId = unit.basicInfo!.unitId!;
  const { homeAccessInstructions, loading } = useHomeAccessInstructions({
    unitId,
  });

  const unitOnboardingInspectionStatus = inspections?.find(
    (inspection: any) =>
      inspection.homeUniqueId === unitId && inspection.type === 'Onboarding',
  )?.status;

  function handleUpdateClick() {
    setIsHomeAccessInstructionsModalOpen(true);
  }

  function handleHomeAccessInstructionsModalDismiss() {
    setIsHomeAccessInstructionsModalOpen(false);
  }

  useEffect(() => {
    const { deliveredBy } = homeAccessInstructions || {};

    if (
      deliveredBy &&
      deliveredBy !== 'Other' &&
      unitOnboardingInspectionStatus &&
      !['Completed', 'Cancelled'].includes(unitOnboardingInspectionStatus)
    ) {
      setPropertySectionTitleVisibilityByProperty((prev) => ({
        ...prev,
        [unitId]: {
          ...prev[unitId],
          'home-access-instructions': true,
        },
      }));
    }
  }, [
    homeAccessInstructions,
    inspections,
    unitId,
    setPropertySectionTitleVisibilityByProperty,
  ]);

  if (loading || isLoadingInspections) {
    return null;
  }

  const { hasAccessInstructionConfirmed, deliveredBy: deliveredByValue } =
    homeAccessInstructions || {};

  const deliveredBy = DELIVERY_METHODS.find(
    (deliveryMethod) => deliveryMethod.key === deliveredByValue,
  )?.text;

  if (!deliveredBy) {
    return null;
  }

  if (
    deliveredBy === 'Other' ||
    ['Completed', 'Cancelled'].includes(unitOnboardingInspectionStatus)
  ) {
    return null;
  }

  return (
    <>
      <Text fontWeight="semibold" className="text-h3-sm">
        {HOME_KEYS_STRINGS['delivery.homeAccess.title']}
      </Text>
      <div className="flex w-full flex-col sm:flex-row md:justify-between md:gap-xl">
        <div
          className={clsx(
            'mb-xl mt-xs flex flex-col items-start justify-between border-0  border-l-3 border-solid pl-xs sm:mt-sm',
            hasAccessInstructionConfirmed ? 'border-blue' : 'border-red',
          )}
        >
          {hasAccessInstructionConfirmed ? (
            <>
              <Text>{deliveredBy}</Text>
              <Text fontWeight="semibold" className="mt-xs text-body">
                {HOME_KEYS_STRINGS['delivery.location']}:{' '}
                {homeAccessInstructions.accessInstructions}
              </Text>
              {Boolean(homeAccessInstructions.lockBoxCode) && (
                <Text fontWeight="semibold" className="mt-0 text-body">
                  {HOME_KEYS_STRINGS['delivery.code']}:{' '}
                  {homeAccessInstructions.lockBoxCode}
                </Text>
              )}
            </>
          ) : (
            <Text className="text-body text-red" fontWeight="semibold">
              {HOME_KEYS_STRINGS['delivery.actionRequired']}
            </Text>
          )}
        </div>
        <div className="mb-xl flex shrink-0 justify-end sm:mt-sm">
          <Button onClick={handleUpdateClick} variant="text">
            {HOME_KEYS_STRINGS['delivery.update']}
          </Button>
        </div>
      </div>
      <HomeAccessInstructionsModal
        homeAccessInstructions={homeAccessInstructions}
        isOpen={isHomeAccessInstructionsModalOpen}
        onDismiss={handleHomeAccessInstructionsModalDismiss}
        unitId={unitId}
      />
    </>
  );
}
